/*
 * lupa.js — coletor de analytics da Lupa (o "Hotjar da casa").
 *
 * Fonte legível — o que vai pro site é o bundle minificado em
 * `site/public/lupa.js` (ver `analytics/build-lupa-js.mjs`). Este arquivo
 * é a dívida paga: quem quiser entender ou auditar o coletor lê AQUI, não
 * o minificado.
 *
 * Contrato do evento (CONTRATOS.md §8):
 *   {t, ts, sid, vid, url, ref, origem, dados}
 *
 * Regras que são REQUISITO, não estilo (ver CLAUDE.md da frente analytics):
 *   - só emite quando `window.top === window.self` (o admin usa iframe pra
 *     desenhar o heatmap por cima da página pública — sem este guard, olhar
 *     o heatmap GERA heatmap, e o dashboard vira nós mesmos olhando nós
 *     mesmos, sem como separar depois);
 *   - `sid` em sessionStorage, `vid` em localStorage, ambos uuid v4 — SEM
 *     cookie, nem de primeira parte;
 *   - `maskAllInputs: true` no rrweb sempre — LGPD, inegociável;
 *   - zero request a terceiro — rrweb e web-vitals vêm EMPACOTADOS no
 *     bundle (o CSP do site não permite script de CDN externo);
 *   - tudo dentro de try/catch — se o coletor falhar, a PÁGINA não quebra
 *     nem atrasa por causa de telemetria;
 *   - ⛔ mas "não quebrar a página" nunca mais significa "falhar calado" no
 *     TRANSPORTE. Um `.catch(function () {})` vazio aqui escondeu por meses
 *     que todo primeiro `replay_chunk` do site era recusado pelo
 *     `sendBeacon` — e levava junto o `page_view`/`scroll`/`click` do mesmo
 *     lote. Lote que não vai vira evento `erro_transporte` (limitado, sem
 *     retry infinito) e entra em `window.lupaAnalytics.diagnostico()`.
 *     Ver o bloco TRANSPORTE dentro de `executar`.
 */

import { record } from "@rrweb/record";
import { onLCP, onCLS, onINP, onFCP, onTTFB } from "web-vitals";

// ---------------------------------------------------------------------------
// Guard de topo — TEM que ser a primeira coisa que o script faz. Se a página
// está dentro de um iframe (o heatmap do admin), não instrumentamos nada.
// ---------------------------------------------------------------------------
(function iniciar() {
  try {
    if (window.top !== window.self) return;
    // Ambiente sem NENHUM transporte: aí sim não há o que tentar. ⚠️ A guarda antiga
    // desistia só por faltar `sendBeacon` — o que fazia sentido quando beacon era o único
    // caminho, e deixou de fazer no instante em que o replay passou a sair por `fetch`.
    // Sem beacon o coletor degrada (perde o flush do descarregamento), não morre.
    if (!navigator.sendBeacon && !window.fetch) return;
  } catch (_e) {
    // acessar window.top pode estourar SecurityError entre origens — trate
    // como "está em iframe" e não emita nada, por segurança.
    return;
  }

  try {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", executar, { once: true });
    } else {
      executar();
    }
  } catch (_e) {
    /* falha calada — nunca deixar o coletor quebrar a página */
  }
})();

function executar() {
  try {
    const config = Object.assign(
      { replay_taxa: 1.0, flush_intervalo_ms: 5000, endpoint: "/api/e" },
      window.LUPA_CONFIG || {}
    );

    const sid = obterOuCriarId(sessionStorage, "lupa_sid");
    const vid = obterOuCriarId(localStorage, "lupa_vid");
    const origem = resolverOrigem();
    const utm = resolverUtm();

    const fila = [];

    function empilhar(t, dados) {
      try {
        fila.push({
          t,
          ts: Date.now(),
          sid,
          vid,
          url: location.pathname,
          ref: document.referrer || "",
          origem,
          dados: dados || {},
        });
      } catch (_e) {
        /* nunca deixar um evento quebrar o resto */
      }
    }

    // =====================================================================
    // TRANSPORTE — dois canais, porque os corpos têm DUAS ordens de grandeza
    // =====================================================================
    //
    // O que estava aqui mandava TUDO por `navigator.sendBeacon`, num lote só.
    // `sendBeacon` recusa corpo acima de 64 KiB (65.536 b) — e `fetch` com
    // `keepalive: true` tem O MESMO teto por especificação, então o "fallback"
    // não era fallback nenhum. Medido em 22/08, corte exato:
    //
    //   64.647 b -> sendBeacon true  · fetch(keepalive) 204 · fetch normal 204
    //   65.671 b -> sendBeacon FALSE · fetch(keepalive) falha · fetch normal 204
    //
    // E TODA página do site passa disso: o primeiro `replay_chunk` de cada
    // carregamento carrega o Meta(4) + FullSnapshot(2) do rrweb, que é o DOM
    // inteiro serializado. Medido: /privacidade 84 KB, / 111 KB,
    // /setor/imobiliarias 222 KB, /cnae 458 KB. Nas 12 medidas, sendBeacon=false.
    //
    // ⛔ O estrago NÃO era só do replay. `replay_chunk` viajava no MESMO lote que
    // `page_view`, `scroll`, `click` e `web_vitals` — quando o lote estourava,
    // TUDO se perdia junto. Era buraco no funil, não só no player. A assinatura
    // ficou no dado de produção: de 20 sessões, 15 sem NENHUM FullSnapshot, e
    // nelas o `seq=0` não existe (menor seq = 1) — o primeiro chunk, o gordo,
    // era o que morria.
    //
    // Por isso os dois canais são SEPARADOS por construção:
    //
    //   leve   (page_view/click/scroll/web_vitals/erro_js/conversão): corpo de
    //          centenas de bytes. Cabe no beacon, e beacon é o único transporte
    //          que sobrevive ao descarregamento da página. Segue por beacon.
    //   replay (replay_chunk): corpo de dezenas a centenas de KB. Sai em
    //          requisição PRÓPRIA, `fetch` SEM keepalive — que não tem teto de
    //          64 KiB. O servidor aceita: o limite dele é 1 MB
    //          (api/ingest/util.py MAX_LOTE_BYTES); medido, 1.050.150 b -> 413.
    //
    // Um chunk gordo nunca mais pode derrubar um `page_view` de 300 bytes.

    // Teto do sendBeacon/fetch-keepalive: 65.536 b. Ficamos abaixo de propósito —
    // o que conta pro navegador é o corpo já serializado, e uma margem de 5 KiB
    // paga qualquer diferença de contagem sem precisar adivinhar.
    const LIMITE_BEACON = 60 * 1024;
    // Teto do SERVIDOR para o corpo QUE VIAJA (api/ingest/util.py MAX_LOTE_BYTES =
    // 1_000_000 -> 413). Abaixo dele com margem: acima disso o corpo não chega, e é
    // melhor DECLARAR a perda aqui do que colecionar 413 que ninguém lê.
    const LIMITE_SERVIDOR = 900_000;
    // Teto do SERVIDOR para o corpo DEPOIS de expandir (MAX_LOTE_DESCOMPRIMIDO_BYTES =
    // 4_000_000 -> 413). ⚠️ Guardar 900.000 comprimidos é OUTRA conta: 900 KB de gzip
    // podem ser 9 MB de JSON, e aí quem recusa é o segundo teto, não o primeiro. Os dois
    // números existem dos dois lados justamente para que o cliente saiba, ANTES de
    // gastar CPU comprimindo, que aquele corpo não seria aceito de nenhum jeito.
    const LIMITE_SERVIDOR_DESCOMPRIMIDO = 3_500_000;

    // =====================================================================
    // COMPRESSÃO — 6,1x a 11,5x medidos sobre os corpos REAIS deste site
    // =====================================================================
    //
    // Medido em 22/08 sobre os primeiros `replay_chunk` de páginas de verdade:
    // /privacidade 6,1x, / 7,4x, /setor/imobiliarias 9,8x, /cnae 11,5x
    // (497.724 b -> 43.179 b). É DOM serializado — texto repetitivo, o alimento
    // preferido do gzip.
    //
    // Isso muda duas coisas concretas, não só a conta de banda:
    //
    //  1. A MARGEM. A pior página do site é `/cnae/secao/c`, com 785.217 b de corpo —
    //     apenas 12,8% abaixo do teto de 900.000. Uma página um pouco mais densa
    //     estouraria, e a perda seria silenciosa para quem publica conteúdo (ninguém
    //     que escreve uma página nova pensa no teto do coletor). Comprimida ela vira
    //     ~87 KB: a margem passa de 12,8% para mais de 10x.
    //  2. O BEACON. Comprimido, um chunk de 400 KB cabe nos 60 KiB do beacon — e o
    //     beacon é o transporte que sobrevive ao `visibilitychange: hidden` no celular,
    //     onde o `fetch` normal pode ser morto junto com a aba.
    //
    // ⚠️ `CompressionStream` NÃO existe em todo navegador (Safari só a partir do 16.4).
    // Sem ele o coletor faz exatamente o que fazia antes — e DIZ que fez, uma vez por
    // carregamento, pelo mesmo `erro_transporte`/`diagnostico()` que já existe. Degradar
    // calado é o defeito que esta frente inteira veio consertar; degradar por falta de
    // API não é exceção.
    const podeComprimir = (function () {
      try {
        return (
          typeof CompressionStream === "function" &&
          typeof Response === "function" &&
          typeof Blob === "function" &&
          typeof Blob.prototype.stream === "function"
        );
      } catch (_e) {
        return false;
      }
    })();

    /** corpo (string) -> Promise<Uint8Array> gzip. */
    function comprimir(corpo) {
      const fluxo = new Blob([corpo]).stream().pipeThrough(new CompressionStream("gzip"));
      return new Response(fluxo).arrayBuffer().then(function (b) {
        return new Uint8Array(b);
      });
    }

    // ---------------------------------------------------------------------
    // Observabilidade da falha — o `.catch(function () {})` vazio que estava
    // aqui é o motivo de isto ter durado meses sem ninguém ver. Transporte que
    // falha PRECISA deixar rastro; mas rastro tem de ser barato e limitado:
    //  - vira um evento `erro_transporte` na fila LEVE, que sai pelo beacon (o
    //    canal que funciona) e cai no bruto do S3 via Firehose — tipo fora de
    //    TIPOS_VALIDOS não entra em agregado nenhum, então não contamina métrica;
    //  - ⛔ no máximo MAX_FALHAS por carregamento: sem retry infinito, sem
    //    tempestade de eventos, sem encher o console de quem visita o site;
    //  - console só quando `LUPA_CONFIG.debug === true`. Para quem depura sem
    //    recarregar, `window.lupaAnalytics.diagnostico()` devolve a lista viva.
    // ---------------------------------------------------------------------
    const MAX_FALHAS = 10;
    const falhas = [];

    function registrarFalha(motivo, detalhe) {
      try {
        const registro = Object.assign({ motivo: String(motivo) }, detalhe || {});
        falhas.push(registro);
        if (falhas.length > MAX_FALHAS) return; // ⛔ teto: falha não vira enxurrada
        empilhar("erro_transporte", registro);
        if (config.debug === true && typeof console !== "undefined" && console.warn) {
          console.warn("[lupa] transporte falhou:", registro);
        }
      } catch (_e) {
        /* nem a observabilidade pode quebrar a página */
      }
    }

    // ⚠️ Aviso ÚNICO de degradação, emitido no carregamento e não na primeira falha:
    // sem `CompressionStream` nada FALHA — o coletor volta ao caminho de antes, que
    // funciona. O que se perde é a margem e o beacon grande, e isso precisa ser
    // MEDÍVEL no bruto (quantos visitantes não comprimem?) em vez de invisível.
    if (!podeComprimir) {
      registrarFalha("sem_compression_stream", { degradou_para: "json_puro" });
    }

    function bytesDe(corpo) {
      try {
        return new Blob([corpo]).size; // UTF-8 real, que é o que o navegador conta
      } catch (_e) {
        return corpo.length;
      }
    }

    /**
     * Posta UM corpo já serializado.
     *
     * ⚠️ Não devolve mais a recusa: com compressão, a recusa pode acontecer DEPOIS
     * (comprimir é assíncrono), e um valor de retorno síncrono não teria como
     * carregá-la. Quem chama passa `aoRecusar(motivo, extras)` e é chamado nos dois
     * tempos, com o mesmo prefixo (`leve_` / `replay_`) de antes.
     *
     * `descarregando` = estamos no pagehide/unload. Aí só `sendBeacon` sobrevive: o
     * navegador pode matar um `fetch` normal junto com o documento — e, pelo mesmo
     * motivo, ⛔ NÃO dá para comprimir ali: `CompressionStream` resolve numa promise, e
     * promise pendente não se completa num documento que está sendo destruído. Por isso
     * o descarregamento continua saindo em JSON puro, e é por isso que existe o
     * despacho antecipado por tamanho lá no `emit` do rrweb: o corpo gordo não pode
     * CHEGAR nesta janela.
     */
    function postar(corpo, descarregando, aoRecusar) {
      const bytes = bytesDe(corpo);

      if (podeComprimir && !descarregando) {
        // O teto que se aplica ao corpo BRUTO é o do descomprimido lá no servidor.
        // Verificado antes de comprimir: gastar CPU num corpo que seria recusado de
        // qualquer jeito é trabalho jogado fora no aparelho do visitante.
        if (bytes > LIMITE_SERVIDOR_DESCOMPRIMIDO) {
          aoRecusar("acima_do_teto_descomprimido_do_servidor", { bytes: bytes });
          return;
        }
        comprimir(corpo)
          .then(function (gz) {
            postarComprimido(gz, bytes, aoRecusar);
          })
          .catch(function (e) {
            // ⛔ Falhou comprimindo: cai para o caminho de sempre, e DIZ que caiu.
            // Silêncio aqui recriaria o `.catch` vazio que custou meses.
            registrarFalha("compressao_falhou", {
              bytes: bytes,
              erro: String((e && e.message) || e).slice(0, 120),
            });
            postarBruto(corpo, false, aoRecusar);
          });
        return;
      }

      postarBruto(corpo, descarregando, aoRecusar);
    }

    /** O caminho de sempre: JSON puro, `text/plain`. É o que roda sem
     *  `CompressionStream` e é o que roda no descarregamento. ⛔ Não mudou. */
    function postarBruto(corpo, descarregando, aoRecusar) {
      const bytes = bytesDe(corpo);

      if (bytes > LIMITE_SERVIDOR) {
        aoRecusar("acima_do_teto_do_servidor", { bytes: bytes });
        return;
      }

      if (bytes <= LIMITE_BEACON && navigator.sendBeacon) {
        const ok = navigator.sendBeacon(
          config.endpoint,
          new Blob([corpo], { type: "text/plain;charset=UTF-8" })
        );
        if (ok) return;
        // beacon recusou mesmo cabendo no teto: fila de saída do navegador cheia.
        // Não é o defeito de tamanho — segue para o fetch abaixo.
        if (descarregando) {
          aoRecusar("beacon_recusou_no_descarregamento", { bytes: bytes });
          return;
        }
      }

      if (descarregando) {
        // ⚠️ Acima do teto do beacon, no descarregamento, NÃO HÁ transporte que
        // se possa prometer: `fetch` normal morre com o documento e
        // `fetch(keepalive)` tem o mesmo teto de 64 KiB do beacon. Então este
        // corpo provavelmente NÃO VAI. Isso é aceitável — o que não é aceitável
        // é fingir que foi, então quem chamou DECLARA a perda.
        aoRecusar("sem_transporte_no_descarregamento", { bytes: bytes });
        return;
      }

      if (!window.fetch) {
        aoRecusar("sem_fetch", { bytes: bytes });
        return;
      }

      // ⛔ SEM keepalive de propósito: keepalive é justamente o que reimpõe o
      // teto de 64 KiB. Sem ele o mesmo corpo de 458 KB passa (medido: 204).
      enviarPorFetch(corpo, { "content-type": "text/plain;charset=UTF-8" }, bytes);
    }

    /**
     * O corpo já comprimido. Aqui o beacon volta a ser possível para corpos que, sem
     * compressão, nunca caberiam nele — e é esse o ganho no `visibilitychange` do
     * celular, onde um `fetch` pode morrer com a aba.
     *
     * ⚠️ Os DOIS cabeçalhos existem porque os dois canais anunciam de jeitos diferentes,
     * e o servidor negocia por cabeçalho (⛔ nunca pelo conteúdo):
     *   fetch   -> `content-encoding: gzip` (+ `content-type: application/gzip`)
     *   beacon  -> `content-type: application/gzip`, e SÓ. `navigator.sendBeacon` não
     *              aceita cabeçalho nenhum; o `type` do Blob é o único controle que ele
     *              dá sobre a requisição. Ignorar essa forma seria abrir mão da
     *              compressão justamente no canal que sobrevive ao descarregamento.
     */
    function postarComprimido(gz, bytesBrutos, aoRecusar) {
      const bytes = gz.byteLength;

      if (bytes > LIMITE_SERVIDOR) {
        aoRecusar("acima_do_teto_do_servidor", { bytes: bytes, bytes_brutos: bytesBrutos });
        return;
      }

      if (bytes <= LIMITE_BEACON && navigator.sendBeacon) {
        const ok = navigator.sendBeacon(
          config.endpoint,
          new Blob([gz], { type: "application/gzip" })
        );
        if (ok) return;
      }

      if (!window.fetch) {
        aoRecusar("sem_fetch", { bytes: bytes, bytes_brutos: bytesBrutos });
        return;
      }

      enviarPorFetch(
        gz,
        { "content-type": "application/gzip", "content-encoding": "gzip" },
        bytes,
        bytesBrutos
      );
    }

    function enviarPorFetch(corpo, cabecalhos, bytes, bytesBrutos) {
      fetch(config.endpoint, {
        method: "POST",
        body: corpo,
        keepalive: false,
        headers: cabecalhos,
      })
        .then(function (r) {
          // 204 é o normal. Qualquer outra coisa (413 dos tetos do servidor, 429 do
          // rate-limit, 5xx) é falha DECLARADA — nunca mais engolida.
          if (!r.ok && r.status !== 204) {
            registrarFalha("http_" + r.status, { bytes: bytes, bytes_brutos: bytesBrutos });
          }
        })
        .catch(function (e) {
          registrarFalha("fetch_falhou", {
            bytes: bytes,
            bytes_brutos: bytesBrutos,
            erro: String((e && e.message) || e).slice(0, 120),
          });
        });
    }

    // ---------------------------------------------------------------------
    // Canal LEVE — page_view, click, scroll, web_vitals, conversão, erro_js.
    // ---------------------------------------------------------------------
    function enviar(descarregando) {
      if (fila.length === 0) return;
      const lote = fila.splice(0, fila.length);
      try {
        const corpo = JSON.stringify({ eventos: lote });
        postar(corpo, descarregando === true, function (motivo, extras) {
          // Um lote LEVE acima de 60 KiB é praticamente impossível (seriam
          // milhares de cliques num carregamento); se acontecer, queremos ver.
          registrarFalha("leve_" + motivo, Object.assign({ eventos: lote.length }, extras));
        });
      } catch (_e) {
        /* perder um lote de telemetria não é incidente — não propague */
      }
    }

    // -------------------------------------------------------------------
    // page_view — carrega `utm_source` quando presente (sessão ou URL), único
    // campo que `api/ingest/util.py::rotulo_origem_trafego` lê para preferir
    // `utm:<fonte>` a `ref:<host>`. Ver `resolverUtm` nos helpers: por que só
    // `utm_source` vai para CÁ (o resto viaja só pelo getter público).
    // -------------------------------------------------------------------
    empilhar("page_view", utm.utm_source ? { utm_source: utm.utm_source } : {});

    // -------------------------------------------------------------------
    // click — delegado no document; seletor curto (tag#id.classe) só pra
    // identificar o alvo no replay/heatmap, nunca conteúdo do elemento.
    // -------------------------------------------------------------------
    document.addEventListener(
      "click",
      function (ev) {
        try {
          empilhar("click", {
            x: ev.pageX,
            y: ev.pageY,
            seletor: seletorCurto(ev.target),
            viewport: { w: window.innerWidth, h: window.innerHeight },
            docH: document.documentElement.scrollHeight,
          });
        } catch (_e) {}
      },
      { capture: true, passive: true }
    );

    // -------------------------------------------------------------------
    // scroll — profundidade máxima da sessão nesta página, em marcos de
    // 25/50/75/100%, com debounce. Não emite um evento por pixel rolado.
    // -------------------------------------------------------------------
    (function instrumentarScroll() {
      let marcoEmitido = 0;
      let pendente = null;
      function medir() {
        pendente = null;
        try {
          const doc = document.documentElement;
          const alturaTotal = doc.scrollHeight - window.innerHeight;
          const profundidade = alturaTotal > 0
            ? Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / alturaTotal) * 100))
            : 100;
          const marco = Math.floor(profundidade / 25) * 25;
          if (marco > marcoEmitido) {
            marcoEmitido = marco;
            empilhar("scroll", { profundidade: marco });
          }
        } catch (_e) {}
      }
      window.addEventListener(
        "scroll",
        function () {
          if (pendente) return;
          pendente = setTimeout(medir, 250);
        },
        { passive: true }
      );
    })();

    // -------------------------------------------------------------------
    // web_vitals — bundlado, sem CDN. Um evento por metrica reportada.
    // -------------------------------------------------------------------
    try {
      const relatar = function (metrica) {
        empilhar("web_vitals", {
          nome: metrica.name,
          valor: metrica.value,
          rating: metrica.rating,
        });
      };
      onLCP(relatar);
      onCLS(relatar);
      onINP(relatar);
      onFCP(relatar);
      onTTFB(relatar);
    } catch (_e) {}

    // -------------------------------------------------------------------
    // erro_js — captura erros não tratados da própria página (não só do
    // coletor). Útil pro time achar página quebrada sem depender do usuário
    // reportar.
    // -------------------------------------------------------------------
    window.addEventListener("error", function (ev) {
      try {
        empilhar("erro_js", {
          mensagem: String(ev.message || "").slice(0, 300),
          arquivo: String(ev.filename || "").slice(0, 200),
          linha: ev.lineno || 0,
        });
      } catch (_e) {}
    });

    // -------------------------------------------------------------------
    // API pública mínima pro resto do site chamar (consulta de CNPJ, envio
    // de e-mail, download de PDF, chat) — o site/agente chama
    // `window.lupaAnalytics.registrar(tipo, dados)` nos pontos de conversão.
    // -------------------------------------------------------------------
    const TIPOS_PUBLICOS = new Set([
      "cnpj_consulta",
      "email_solicitado",
      "pdf_download",
      "chat_inicio",
      "chat_msg",
    ]);
    window.lupaAnalytics = {
      registrar: function (tipo, dados) {
        try {
          if (!TIPOS_PUBLICOS.has(tipo)) return;
          empilhar(tipo, dados);
        } catch (_e) {}
      },
      // Envia AGORA, por sendBeacon (que sobrevive a navegacao). Existe porque os
      // eventos de conversao acontecem imediatamente antes de sair da pagina: a
      // ilha de consulta registra `cnpj_consulta` e no gesto seguinte faz
      // `location.href = /r/{token}`. O lote so seria enviado no proximo tick do
      // intervalo, e morria com a navegacao — medido: 0 chaves `cnpj#` no
      // agregado com 44 emissoes gravadas no servidor. Quem converte tem de
      // descarregar antes de sair.
      enviar: function () {
        try { enviar(false); } catch (_e) {}
      },
      // Falhas de transporte deste carregamento, sem precisar recarregar nem
      // sujar o console de quem visita. É a versão local do `erro_transporte`
      // que já viaja pro bruto do S3 — mesma lista, dois alcances.
      diagnostico: function () {
        try { return falhas.slice(); } catch (_e) { return []; }
      },
      // UTM da sessão (`utm_source`/`utm_medium`/`utm_campaign`, só as chaves
      // presentes), já sanitizado — ver `resolverUtm`. Existe para o formulário
      // de lead (`POST /api/email`, `api/consulta/handler.py::_handle_email`)
      // montar o campo `utm` sem reimplementar leitura de querystring nem de
      // `sessionStorage`: o handler já aceita `{token, email, sid, utm,
      // pagina_origem}` (`item_lead["utm"] = utm` quando é dict não-vazio) —
      // falta só o formulário (fora do território desta frente, em
      // `site/src/pages/r/index.astro`) chamar isto e mandar junto.
      utm: function () {
        try { return Object.assign({}, utm); } catch (_e) { return {}; }
      },
    };

    // -------------------------------------------------------------------
    // replay (rrweb) — amostragem configurável, maskAllInputs sempre.
    // Chunks de N eventos rrweb viram UM evento `replay_chunk`.
    // -------------------------------------------------------------------
    let despacharChunk = function () {};
    try {
      if (Math.random() < Number(config.replay_taxa)) {
        let bufferRR = [];
        let bytesBuffer = 0;
        let seq = 0;
        const TAMANHO_CHUNK = 40;
        // ⚠️ O TETO QUE FAZ A PERDA DO `pagehide` PARAR DE ACONTECER.
        //
        // No descarregamento não existe compressão (promise não resolve num documento
        // que morre) e não existe transporte acima de 64 KiB. Então a única forma de o
        // `pagehide` não perder nada é o corpo gordo NUNCA CHEGAR nele. Isto despacha o
        // buffer assim que ele passa de 40 KiB — com a página VIVA, onde `fetch` e
        // compressão funcionam —, de modo que o que sobra para o descarregamento cabe
        // no beacon por construção.
        //
        // ⛔ Antes, a perda era honestamente DECLARADA (`replay_sem_transporte_no_
        // descarregamento`), que é melhor que mentir e ainda assim é perda. Declarar bem
        // não é o objetivo; o objetivo é não perder.
        //
        // 40 KiB e não 60: o envelope do evento (`t/ts/sid/vid/url/ref/origem`) e o
        // JSON.stringify de um objeto contam um pouco diferente de `new Blob().size`
        // (UTF-16 vs UTF-8 nos acentos), e a margem paga a diferença sem adivinhar.
        const LIMITE_BUFFER_RR = 40 * 1024;

        // ⛔ O chunk NÃO entra mais na `fila` leve (era o `empilhar` daqui que
        // amarrava os dois destinos no mesmo corpo). Cada chunk é o seu próprio
        // lote, na sua própria requisição, pelo canal que aguenta o tamanho.
        despacharChunk = function (descarregando) {
          if (bufferRR.length === 0) return;
          const eventosRR = bufferRR;
          bufferRR = [];
          bytesBuffer = 0;
          const evento = {
            t: "replay_chunk",
            ts: Date.now(),
            sid: sid,
            vid: vid,
            url: location.pathname,
            ref: document.referrer || "",
            origem: origem,
            dados: { seq: seq++, eventos_rrweb: eventosRR },
          };
          try {
            const corpo = JSON.stringify({ eventos: [evento] });
            postar(corpo, descarregando === true, function (motivo, extras) {
              // ⚠️ O chunk NÃO foi. Declarado, com o número que explica por quê —
              // e a declaração viaja pelo canal leve, que sobrevive ao pagehide.
              registrarFalha("replay_" + motivo, Object.assign({
                seq: evento.dados.seq,
                url: evento.url,
                // ⚠️ `rrweb_perdidos` é uma CONTAGEM, e o nome diz isso. Chamava-se
                // `eventos_rrweb` — o mesmo nome que, dentro de `dados` de um
                // `replay_chunk`, é a LISTA de eventos. Um consumidor que lesse os dois
                // pelo nome faria `.map()` num número; foi exatamente o que aconteceu com
                // o diário do gate, que marcou como "corpo ilegível" justamente os dois
                // lotes que carregavam a declaração da perda. Tipo:valor atômico, e o
                // nome carregando a unidade.
                rrweb_perdidos: eventosRR.length,
              }, extras));
            });
          } catch (_e) {
            registrarFalha("replay_serializacao", { seq: evento.dados.seq });
          }
        };

        record({
          emit(evento) {
            bufferRR.push(evento);
            // ⚠️ O FullSnapshot (type 2) é o evento gordo — é ele que carrega o
            // DOM inteiro, é ele que fazia o lote estourar, e é ele SEM O QUAL o
            // replay não tem primeiro quadro. Despachar na hora, em vez de
            // esperar 40 eventos ou 5 s, tira justamente o corpo mais caro da
            // janela em que a página pode ser descarregada — que é a janela em
            // que não há transporte capaz de levá-lo.
            if (evento && evento.type === 2) {
              despacharChunk(false);
              return;
            }
            // Estimativa, não medida exata: `JSON.stringify` de UM evento custa o
            // tamanho dele, e somar por evento é O(n) no total — enquanto medir o
            // buffer inteiro a cada `emit` seria O(n²). A margem de 20 KiB até o teto
            // do beacon é justamente o que compra o direito de estimar.
            try {
              bytesBuffer += JSON.stringify(evento).length;
            } catch (_e) {
              bytesBuffer += 1024; // evento que nem serializa: conte caro, nunca barato
            }
            if (bufferRR.length >= TAMANHO_CHUNK || bytesBuffer >= LIMITE_BUFFER_RR) {
              despacharChunk(false);
            }
          },
          maskAllInputs: true, // LGPD — inegociável, nunca desligar
          checkoutEveryNms: 30000,
        });
        setInterval(function () { despacharChunk(false); }, config.flush_intervalo_ms);
      }
    } catch (_e) {
      /* replay é bônus — se o rrweb falhar, o resto do coletor segue vivo */
    }

    // -------------------------------------------------------------------
    // flush: intervalo + visibilitychange + pagehide
    //
    // ⚠️ A ORDEM importa nos dois últimos: o replay vai PRIMEIRO porque, quando
    // ele não cabe, `despacharChunk` empilha um `erro_transporte` na fila leve —
    // e é o `enviar` logo abaixo, por sendBeacon, que carrega essa declaração
    // para fora. Invertido, a perda ficaria muda outra vez.
    // -------------------------------------------------------------------
    setInterval(function () { enviar(false); }, config.flush_intervalo_ms);
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState !== "hidden") return;
      // `hidden` não é destruição do documento: a página segue viva (troca de
      // aba, minimizar) e um `fetch` normal ainda completa. Então aqui o replay
      // usa o canal grande de verdade — é o que salva o chunk em celular, onde
      // `pagehide` muitas vezes nem chega.
      despacharChunk(false);
      // `false` aqui é deliberado e NÃO significa "não urgente": `postar` tenta o beacon
      // primeiro de qualquer jeito quando o corpo cabe. O que `false` acrescenta é o
      // fetch de reserva se o beacon recusar — reserva que existe porque a página segue
      // viva. Com `true` a gente jogaria fora o segundo caminho sem ganhar nada.
      enviar(false);
    });
    window.addEventListener("pagehide", function () {
      // Aqui o documento está indo embora: só `sendBeacon` sobrevive, e ele para
      // em 64 KiB. Chunk que não couber é DECLARADO perdido, não fingido.
      despacharChunk(true);
      enviar(true);
    });
  } catch (_e) {
    /* qualquer coisa que escapou dos try/catch internos ainda cai aqui */
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function uuidV4() {
  // crypto.randomUUID cobre todo navegador moderno; fallback manual só por
  // segurança (Safari antigo / contexto sem crypto.randomUUID).
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  (window.crypto || {}).getRandomValues
    ? window.crypto.getRandomValues(bytes)
    : bytes.forEach(function (_v, i) { bytes[i] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, function (b) { return b.toString(16).padStart(2, "0"); });
  return (
    hex.slice(0, 4).join("") + "-" + hex.slice(4, 6).join("") + "-" +
    hex.slice(6, 8).join("") + "-" + hex.slice(8, 10).join("") + "-" +
    hex.slice(10, 16).join("")
  );
}

function obterOuCriarId(armazenamento, chave) {
  try {
    let v = armazenamento.getItem(chave);
    if (!v) {
      v = uuidV4();
      armazenamento.setItem(chave, v);
    }
    return v;
  } catch (_e) {
    // storage bloqueado (modo privado agressivo etc): id de sessão volátil,
    // não persiste entre eventos — degrada, não quebra.
    return uuidV4();
  }
}

function resolverOrigem() {
  try {
    const params = new URLSearchParams(location.search);
    const daUrl = params.get("origem");
    if (daUrl === "interno") {
      sessionStorage.setItem("lupa_origem", "interno");
      return "interno";
    }
    const persistida = sessionStorage.getItem("lupa_origem");
    return persistida === "interno" ? "interno" : "publico";
  } catch (_e) {
    return "publico";
  }
}

// Chaves UTM capturadas. `utm_source` é a ÚNICA que
// `api/ingest/util.py::rotulo_origem_trafego` lê (prefere `utm:<fonte>` a
// `ref:<host>` no agregado `origem#<dia>`) — as outras duas viajam só pelo
// getter público `window.lupaAnalytics.utm()`, para quem monta o `utm` (dict
// livre) de `POST /api/email`/`POST /api/contato`
// (`api/consulta/handler.py::_handle_email`/`_handle_contato`, que já aceitam
// e gravam o dict como veio, sem validar chave).
const UTM_CHAVES = ["utm_source", "utm_medium", "utm_campaign"];
// Sanea na ORIGEM — não confiar no teto do servidor (`rotulo_origem_trafego`
// só corta os 64 primeiros chars do RÓTULO agregado; o `utm` bruto do lead
// não tem teto nenhum do lado do servidor). Espaço nas pontas fora, tamanho
// limitado: um `utm_campaign` de propaganda mal-configurada não vira KB de
// lixo no evento nem no item do DynamoDB.
const UTM_LIMITE = 128;

function sanitizarUtm(valor) {
  if (typeof valor !== "string") return "";
  return valor.trim().slice(0, UTM_LIMITE);
}

/**
 * UTM da entrada — lida da querystring da URL ATUAL e, se ausente ali,
 * cai para o que a sessão já tinha guardado.
 *
 * ⚠️ Por que persistir em sessionStorage, igual `resolverOrigem` faz com
 * `lupa_origem`: a UTM só vem na URL da PRIMEIRA página (quem clica no link
 * do ChatGPT/anúncio cai numa URL com `?utm_source=...`; a navegação seguinte
 * dentro do site não carrega mais nada). Sem persistir, o `page_view` da
 * segunda página perderia a atribuição e cairia para `ref:<host>` — e o
 * `ref` de uma navegação INTERNA é o próprio site, não a origem real da
 * visita.
 *
 * ⚠️ Uma URL de entrada COM utm_source substitui a UTM da sessão (é um novo
 * touch — o mesmo racional de `resolverOrigem` para `?origem=interno`), não
 * soma com a antiga.
 *
 * Degrada em silêncio: sessionStorage bloqueado (modo privado agressivo)
 * ainda deixa ler a URL da página atual — só não persiste entre páginas.
 */
function resolverUtm() {
  const daUrl = {};
  try {
    const params = new URLSearchParams(location.search);
    UTM_CHAVES.forEach(function (chave) {
      const v = sanitizarUtm(params.get(chave));
      if (v) daUrl[chave] = v;
    });
  } catch (_e) {
    return {};
  }

  if (Object.keys(daUrl).length > 0) {
    try {
      UTM_CHAVES.forEach(function (chave) {
        if (daUrl[chave]) sessionStorage.setItem("lupa_utm_" + chave, daUrl[chave]);
        else sessionStorage.removeItem("lupa_utm_" + chave);
      });
    } catch (_e) {
      /* sessionStorage bloqueado: usa só a URL desta página, não persiste */
    }
    return daUrl;
  }

  const persistida = {};
  try {
    UTM_CHAVES.forEach(function (chave) {
      const v = sessionStorage.getItem("lupa_utm_" + chave);
      if (v) persistida[chave] = v;
    });
  } catch (_e) {
    return {};
  }
  return persistida;
}

function seletorCurto(elemento) {
  try {
    if (!elemento || elemento.nodeType !== 1) return "";
    const tag = elemento.tagName.toLowerCase();
    const id = elemento.id ? "#" + elemento.id : "";
    const classe = elemento.classList && elemento.classList.length
      ? "." + Array.from(elemento.classList).slice(0, 2).join(".")
      : "";
    return (tag + id + classe).slice(0, 80);
  } catch (_e) {
    return "";
  }
}
